import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, DestroyRef } from '@angular/core';
import { UntypedFormBuilder, Validators, ValidationErrors, UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { AnalyticValidators } from 'src/app/core/validators/analytic.validators';
import { AnalyticEdit } from '../model/analyticEdit';
import { Organization } from 'src/app/core/model/organization';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { Subscription } from 'rxjs';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  standalone: false,
  selector: 'app-analytic-edit',
  templateUrl: './analytic-edit.component.html',
  styleUrls: ['./analytic-edit.component.scss']
})
export class AnalyticEditComponent implements OnInit, OnDestroy
{
  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<AnalyticEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public allOrganizationsData = [];
  public organizationsFiltered: Organization[] = [];
  public virtualOrganization: any = { itemHeight: 30 };

  public urlPattern = /^(?:https?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;

  private analyticEdit: AnalyticEdit;
  
  private formFieldSubscriptions = new Subscription();  

  constructor(
    private fb: UntypedFormBuilder,
    private analyticValidators: AnalyticValidators,
    private organizationService: OrganizationService,
    private messageHandlerService: MessageHandlerService,
    private readonly destroyRef: DestroyRef) { }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get description(): UntypedFormControl
  {
    return this.editForm.get('description');
  }

  public get link(): UntypedFormControl
  {
    return this.editForm.get('link');
  }

  public get enabled(): UntypedFormControl
  {
    return this.editForm.get('enabled');
  }

  public get organizations(): UntypedFormControl
  {
    return this.editForm.get('organizations');
  }

  public get allOrganizations(): UntypedFormControl
  {
    return this.editForm.get('allOrganizations');
  }

  @Input() public set model(analytic: AnalyticEdit)
  {
    this.initModel(analytic);
  }

  public ngOnInit(): void
  {
    this.organizationService.getCachedOrganizations().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) =>
      {
        this.allOrganizationsData = data;
        this.filterOrganizations('');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public ngOnDestroy(): void
  {
    this.formFieldSubscriptions.unsubscribe();
  }  

  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = this.editForm.value;
    const allOrganizations = objectToPersist.allOrganizations === undefined || objectToPersist.allOrganizations === null
      ? false
      : objectToPersist.allOrganizations;
    objectToPersist.allOrganizations = allOrganizations;

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  public isValidUri(uri: UntypedFormControl): boolean
  {
    const regEx = new RegExp(this.urlPattern);
    return uri.value !== undefined && uri.value && regEx.test(uri.value);
  }

  /**
   * Filters the organizations based on user input.
   */
  public filterOrganizations(filter: string): void
  {
    this.organizationsFiltered = this.allOrganizationsData
      .filter(fa => fa.name.toLowerCase().includes(filter.toLowerCase()));
  }

  public analyticItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(analyticEdit: AnalyticEdit)
  {
    if (analyticEdit === undefined)
    {
      return;
    }

    this.formFieldSubscriptions.unsubscribe();
    this.formFieldSubscriptions = new Subscription();

    // Set the current analytic id used for name async validators
    const previousAnalyticName = this.isNew ? '' : analyticEdit.name;

    const urlValidatorsRequired = [
      Validators.required,
      Validators.pattern(this.urlPattern)
    ];

    const organizations: Organization[] = [];
    if (analyticEdit.organizations !== null && analyticEdit.organizations !== undefined)
    {
      analyticEdit.organizations.forEach((org: any) =>
      {
        const fullOrganization: Organization = this.organizationsFiltered.find(x => x.id === org.organizationId);
        if (fullOrganization)
        {
          organizations.push(fullOrganization);
        }
      });
    }

    const allOrganizationsChecked = this.isNew || analyticEdit.allOrganizations ? true : false;

    // Set form
    this.editForm = this.fb.group({
      id: [analyticEdit.id],
      version: [analyticEdit.version],
      name: [
        { value: '', disabled: false },
        {
          validators: [Validators.required],
          asyncValidators: (): Observable<ValidationErrors | null> =>
            this.analyticValidators.validateAnalyticNameUnique(this.editForm.get('name'), previousAnalyticName)
        }
      ],
      link: [
        { value: analyticEdit.link, disabled: false },
        {
          validators: urlValidatorsRequired
        }
      ],
      description: { value: analyticEdit.description, disabled: false },
      enabled: [analyticEdit.enabled !== null ? analyticEdit.enabled : true, Validators.required],
      organizations: [{ value: organizations, disabled: false }],
      allOrganizations: {
        value: allOrganizationsChecked,
        disabled: false
      },
    });

    const allOrganizations = this.editForm.controls.allOrganizations.value === undefined || this.editForm.controls.allOrganizations.value === null ? false : this.editForm.controls.allOrganizations.value;
    if (allOrganizations === true)
    {
      this.editForm.get('organizations').disable();
    }

    this.editForm.get('allOrganizations').valueChanges.subscribe({
      next: (value: boolean) =>
      {
        if (value)
        {
          this.editForm.get('organizations').disable();
          this.editForm.get('organizations').setValue(null);
        }
        else
        {
          this.editForm.get('organizations').enable();
        }
      }
    });

    this.editForm.reset({
      ...analyticEdit,
      allOrganizations: allOrganizationsChecked
    });
    this.analyticEdit = analyticEdit;
    this.editFormActive = analyticEdit !== undefined;
  }
}
